import { action, makeObservable, observable } from 'mobx';
import {
  betStore,
  BetStatus,
  RoundStatus,
  freeBetStore,
  userInfoStore,
  roundInfoStore,
  gameConfigStore,
  applicationStore,
  notificationStore,
  parseNumberByPrecision,
} from '@pascal/pascal-core';

import {
  minOdd,
  betActionName,
  defaultMinOdd,
  autoBetDataKeys,
  autoCashOutDataKeys,
} from '../enums/uiConfig';
import { gameStore } from './gameStore';
import { gameLiveConnection } from '../services';
import { checkDoBetModel } from '../helpers/supporters';
import { divide, multiply, toFixed } from '../helpers/arithmeticOperations';

class DoBetStore {
  totalBetsAmount = 0;

  currentBetAmount = null;

  currentWinAmountData = { 0: 0, 1: 0, 2: 0 };

  activeMaxPayoutPopData = {
    isShowMaxPayOutPopUp: false,
    maxPayout: null,
    isFirst: true,
  };

  autoBetData = [
    {
      isAutoBet: false,
      isOpened: false,
      autoBetCount: 0,
      fighterNumber: 0,
    },
    {
      isAutoBet: false,
      isOpened: false,
      autoBetCount: 0,
      fighterNumber: 1,
    },
    {
      isAutoBet: false,
      isOpened: false,
      autoBetCount: 0,
      fighterNumber: 2,
    },
  ];

  autoCashOutData = [
    {
      isOpened: false,
      fighterNumber: 0,
      isValidatedOdd: false,
      isBlurred: false,
      autoCashOutOdd: defaultMinOdd,
      autoCashOutMaxOdd: divide(
        gameConfigStore.limits.maxWin,
        gameConfigStore.limits?.defaultBetAmount || gameConfigStore.limits?.minBet
      ),
    },
    {
      isOpened: false,
      fighterNumber: 1,
      isValidatedOdd: false,
      isBlurred: false,
      autoCashOutOdd: defaultMinOdd,
      autoCashOutMaxOdd: divide(
        gameConfigStore.limits.maxWin,
        gameConfigStore.limits?.defaultBetAmount || gameConfigStore.limits?.minBet
      ),
    },
    {
      isOpened: false,
      fighterNumber: 2,
      isValidatedOdd: false,
      isBlurred: false,
      autoCashOutOdd: defaultMinOdd,
      autoCashOutMaxOdd: divide(
        gameConfigStore.limits.maxWin,
        gameConfigStore.limits?.defaultBetAmount || gameConfigStore.limits?.minBet
      ),
    },
  ];

  betData = [
    {
      betId: null,
      fighterNumber: 0,
      amount: gameConfigStore.limits?.defaultBetAmount || gameConfigStore.limits?.minBet,
      isNextRoundBet: false,
      isBetAccepted: false,
      freeBetId: null,
      freeBetMinOdd: null,
      winAmount: null,
      halfWinAmount: null,
      finishedOdd: null,
      isDisabled: false,
      isHalfCashOut: false,
    },
    {
      betId: null,
      fighterNumber: 1,
      amount: gameConfigStore.limits?.defaultBetAmount || gameConfigStore.limits?.minBet,
      isNextRoundBet: false,
      isBetAccepted: false,
      freeBetId: null,
      freeBetMinOdd: null,
      winAmount: null,
      halfWinAmount: null,
      finishedOdd: null,
      isDisabled: false,
      isHalfCashOut: false,
    },
    {
      betId: null,
      fighterNumber: 2,
      amount: gameConfigStore.limits?.defaultBetAmount || gameConfigStore.limits?.minBet,
      isNextRoundBet: false,
      isBetAccepted: false,
      freeBetId: null,
      freeBetMinOdd: null,
      winAmount: null,
      halfWinAmount: null,
      finishedOdd: null,
      isDisabled: false,
      isHalfCashOut: false,
    },
  ];

  constructor() {
    makeObservable(this, {
      betData: observable.deep,
      totalBetsAmount: observable,
      autoCashOutData: observable,
      currentBetAmount: observable,
      autoBetData: observable.deep,
      currentWinAmountData: observable.deep,
      activeMaxPayoutPopData: observable.deep,
      setWinData: action.bound,
      sendCashOut: action.bound,
      doBetSender: action.bound,
      setBetAmount: action.bound,
      resetAutoBet: action.bound,
      setFreeBetData: action.bound,
      resetDoBetData: action.bound,
      resetGameStates: action.bound,
      setIsHalfCashOut: action.bound,
      updatedRoundData: action.bound,
      setAutoCashOutOdd: action.bound,
      updateAutoBetData: action.bound,
      updateBetAccepted: action.bound,
      handleDoBetResult: action.bound,
      handleOnRoundStart: action.bound,
      setInitialBetAmount: action.bound,
      updateInitialBetData: action.bound,
      handleClearRoundData: action.bound,
      resetAutoCashuOutData: action.bound,
      updateAutoCashOutData: action.bound,
      setFighterFinishedOdd: action.bound,
      handleBetForNextRound: action.bound,
      updateTotalBetsAmount: action.bound,
      checkMaxWinFunctional: action.bound,
      updateIsDisabledBetSection: action.bound,
      setActiveMaxPayoutPopUpData: action.bound,
      resetActiveMaxPayoutPopUpData: action.bound,
    });
  }

  setCurrentBetAmount(amount) {
    this.currentBetAmount = amount;
  }

  updatedRoundData() {
    this.betData = this.betData.map((item) => {
      if (typeof item.isFirstUpdatedSection !== 'undefined') {
        delete item.isFirstUpdatedSection;
        return item;
      }
      return item;
    });
  }

  checkMaxWinFunctional(myBetsData) {
    if (myBetsData) {
      myBetsData.forEach((item) => {
        if (item.betId) {
          if (
            !item.finishedOdd &&
            gameStore.oddData > minOdd &&
            !item.winAmount &&
            !item.halfWinAmount
          ) {
            this.currentWinAmountData[item.fighterNumber] = toFixed(
              multiply(gameStore.currentJetsInfo[item.fighterNumber], Number(item.amount)),
              2
            );
          }

          if (!item.finishedOdd && item.halfWinAmount && !item.winAmount) {
            this.currentWinAmountData[item.fighterNumber] = toFixed(
              item.halfWinAmount +
                toFixed(
                  multiply(gameStore.currentJetsInfo[item.fighterNumber], Number(item.amount) / 2),
                  2
                ),
              2
            );
          }

          if (!item.finishedOdd && item.winAmount) {
            this.currentWinAmountData[item.fighterNumber] = item.winAmount;
          }

          if (item.finishedOdd && !item.winAmount && item.halfWinAmount) {
            this.currentWinAmountData[item.fighterNumber] = item.halfWinAmount;
          }

          if (item.finishedOdd && !item.winAmount && !item.halfWinAmount) {
            this.currentWinAmountData[item.fighterNumber] = 0;
          }
        }
      });
      gameStore.setIsShowMaxWinPopUpData();
    } else {
      this.currentWinAmountData = { 0: 0, 1: 0, 2: 0 };
      gameStore.setIsShowMaxWinPopUpData(true);
    }
  }

  setInitialBetAmount(amount) {
    this.betData = this.betData.map((item) => ({ ...item, amount }));
  }

  setFighterFinishedOdd(jetInfo) {
    this.betData = this.betData.map((item) => {
      if (item.fighterNumber === jetInfo.index) {
        return {
          ...item,
          finishedOdd: jetInfo.odd,
        };
      }
      return item;
    });
  }

  setIsHalfCashOut(isHalf, index) {
    if (index || index === 0) {
      this.betData = this.betData.map((section) =>
        section.fighterNumber === index ? { ...section, isHalfCashOut: isHalf } : section
      );
    } else {
      this.betData = this.betData.map((section) => {
        return { ...section, isHalfCashOut: isHalf };
      });
    }
  }

  setActiveMaxPayoutPopUpData(isShowMaxPayOutPopUp, maxPayout, isFirst) {
    this.activeMaxPayoutPopData = { maxPayout, isFirst, isShowMaxPayOutPopUp };
  }

  resetActiveMaxPayoutPopUpData(isReset, isFirst) {
    this.activeMaxPayoutPopData = isReset
      ? {
          isShowMaxPayOutPopUp: false,
          maxPayout: null,
          isFirst: true,
        }
      : { ...this.activeMaxPayoutPopData, isFirst };
  }

  setFreeBetData(freeBetMinOdd, freeBetId, freeBetMaxPayout, freeBetType, jetIndex) {
    this.betData = this.betData.map((item) => {
      if (item.fighterNumber === jetIndex) {
        return {
          ...item,
          freeBetId,
          freeBetType,
          freeBetMinOdd,
          freeBetMaxPayout,
        };
      }
      return item;
    });
  }

  updateTotalBetsAmount(amount, isReset = false) {
    if (isReset) {
      this.totalBetsAmount = 0;
    } else {
      this.totalBetsAmount += Number(amount);
    }
  }

  updateAutoBetData(key, value, jetIndex) {
    this.autoBetData = this.autoBetData.map((item) => {
      if (item.fighterNumber === jetIndex) {
        return {
          ...item,
          [key]: value,
        };
      }
      return item;
    });
  }

  resetAutoBet() {
    this.autoBetData = this.autoBetData.map((item) => ({
      ...item,
      isAutoBet: false,
      isOpened: false,
      autoBetCount: 0,
    }));
  }

  resetAutoCashuOutData() {
    this.autoCashOutData = this.autoCashOutData.map((item) => ({
      ...item,
      isOpened: false,
      isValidatedOdd: false,
      isBlurred: false,
      autoCashOutOdd: defaultMinOdd,
      autoCashOutMaxOdd: divide(
        gameConfigStore.limits.maxWin,
        gameConfigStore.limits?.defaultBetAmount || gameConfigStore.limits?.minBet
      ),
    }));
  }

  updateIsDisabledBetSection(isDisabledBetSection, jetIndex) {
    if (jetIndex || jetIndex === 0) {
      this.betData = this.betData.map((item) => {
        if (item.fighterNumber === jetIndex) {
          return {
            ...item,
            isDisabled: isDisabledBetSection,
          };
        }
        return item;
      });
    } else {
      this.betData = this.betData.map((item) => ({ ...item, isDisabled: isDisabledBetSection }));
    }
  }

  setBetAmount(amount) {
    this.betData = this.betData.map((item) => {
      if (item.fighterNumber === gameStore.currentFighterSection) {
        return {
          ...item,
          amount,
        };
      }
      return item;
    });
  }

  resetDoBetData() {
    this.betData = this.betData.map((item) => {
      return {
        ...item,
        amount: gameConfigStore.limits.defaultBetAmount || gameConfigStore.limits.minBet || 0,
        isNextRoundBet: false,
        isBetAccepted: false,
      };
    });

    this.autoCashOutData = this.autoCashOutData.map((item) => ({
      fighterNumber: item.fighterNumber,
      isOpened: false,
      autoCashOutOdd: defaultMinOdd,
    }));
  }

  updateBetAccepted(isBetAccepted, betId, jetIndex) {
    this.betData = this.betData.map((item) => {
      if (item.fighterNumber === jetIndex) {
        return {
          ...item,
          isBetAccepted,
          betId,
        };
      }
      return item;
    });
  }

  setAutoCashOutOdd(autoCashOutOdd) {
    this.autoCashOutData = this.autoCashOutData.map((item) => {
      if (item.fighterNumber === gameStore.currentFighterSection) {
        return {
          ...item,
          autoCashOutOdd,
        };
      }
      return item;
    });
  }

  updateAutoCashOutData(key, value, jetIndex) {
    const { activeFreeBet } = freeBetStore;
    this.autoCashOutData = this.autoCashOutData.map((item) => {
      if (item.fighterNumber === jetIndex) {
        return {
          ...item,
          ...(key === autoCashOutDataKeys.isOpened && !item.isOpened
            ? { autoCashOutOdd: activeFreeBet?.id ? activeFreeBet?.minOdd : defaultMinOdd }
            : {}),
          [key]: value,
        };
      }
      return item;
    });
  }

  updateInitialBetData(data, jetIndex, freeBets = []) {
    this.betData = this.betData.map((item) => {
      if (item.fighterNumber === jetIndex) {
        return {
          betId: data.id,
          fighterNumber: item.fighterNumber,
          amount: data.betData?.cashOutInfos?.length
            ? parseNumberByPrecision(data.betData?.cashOutInfos[0]?.amount, 2)
            : data.amount,
          isNextRoundBet: false,
          isBetAccepted: data.status === BetStatus.Pending,
          freeBetId: data.freeBetId,
          freeBetMinOdd: freeBets.find((el) => el.id === data.freeBetId)?.minOdd,
          winAmount: data.betData.cashOutInfos.length === 1 ? null : data.winAmount,
          isHalfCashOut: !!data.betData.cashOutInfos.length,
          halfWinAmount: data.betData.cashOutInfos.length === 1 ? data.winAmount : null,
          finishedOdd: item.finishedOdd,
          isDisabled: false,
        };
      }
      return item;
    });

    if (data?.betData.autoCashoutOdd) {
      this.autoCashOutData = this.autoCashOutData.map((item) => {
        if (item.fighterNumber === data.betData.index) {
          return {
            ...item,
            isOpened: true,
            autoCashOutOdd: data.betData.autoCashoutOdd,
          };
        }
        return item;
      });
    }
  }

  doBetSender(jetIndex) {
    const { limits } = gameConfigStore;
    const { balance: userBalance } = userInfoStore;
    const fightersData = this.betData;
    const { gameTypeId } = gameConfigStore;
    const { isDemo } = applicationStore;
    const autoBetData = this.autoBetData;
    const { activeFreeBet } = freeBetStore;
    const autoCashOutData = this.autoCashOutData;

    const amount = fightersData.find((item) => item.fighterNumber === jetIndex).amount;
    const isAutoBet = autoBetData.find((item) => item.fighterNumber === jetIndex).isAutoBet;
    const fighterAutoCashOutData = autoCashOutData.find((item) => item.fighterNumber === jetIndex);

    const responseCode = checkDoBetModel(limits, userBalance, amount, isDemo);

    if (responseCode) {
      notificationStore.setNotificationType({
        responseCode,
      });

      if (isAutoBet) {
        this.resetAutoBet();
      }
    } else {
      const doBetModel = {
        amount,
        roundId: roundInfoStore.roundId,
        gameTypeId,
        freeBetId: activeFreeBet?.id && !isDemo ? activeFreeBet?.id : null,
        betData: {
          index: jetIndex,
          autoCashoutOdd: fighterAutoCashOutData.isOpened
            ? fighterAutoCashOutData.autoCashOutOdd
            : 0,
        },
      };

      this.updateIsDisabledBetSection(true, jetIndex);
      // if (isDemo) {
      //   const currentDemoBetData = {
      //     ...doBetModel,
      //     status: BetStatus.Pending,
      //     id: createGuid(),
      //     date: new Date(),
      //     responseCode: BetResponseCode.Success,
      //   };
      //   if (currentDemoBetData.amount <= userInfoStore.balance) {
      //     this.decrementBalance(amount);
      //     this.updateTotalBetsAmount(amount);
      //     this.handleDoBetResult(currentDemoBetData);
      //     popupStore.updateDemoBetsCount();
      //   }
      // } else {
      gameLiveConnection.doBet(doBetModel);
      // }
    }
  }

  handleOnRoundStart() {
    const autoBetData = this.autoBetData;

    this.betData = this.betData.map((item) => ({
      ...item,
      isBetAccepted: false,
      winAmount: 0,
      isHalfCashOut: false,
      halfWinAmount: 0,
    }));

    const nextRoundData = this.betData.filter((item) => item.isNextRoundBet);

    if (nextRoundData.length) {
      nextRoundData.forEach((item) => {
        this.doBetSender(item.fighterNumber);
        this.betData = this.betData.map((fighterBetData) => {
          if (fighterBetData.fighterNumber === item.fighterNumber) {
            return {
              ...fighterBetData,
              isNextRoundBet: false,
              isFirstUpdatedSection: true,
            };
          }
          return fighterBetData;
        });
      });
    }

    autoBetData.forEach((data) => {
      if (data.isAutoBet && data.autoBetCount) {
        const isDisabled = this.betData.find((el) => el.fighterNumber === data.fighterNumber);
        !isDisabled && this.updateIsDisabledBetSection(true, data.fighterNumber);
        this.doBetSender(data.fighterNumber);
      }
    });
  }

  handleBetForNextRound(index) {
    this.betData = this.betData.map((item) => {
      if (item.fighterNumber === index) {
        return {
          ...item,
          isDisabled: true,
          isNextRoundBet: !item.isNextRoundBet,
        };
      }
      return item;
    });

    // const nextSection = this.betData.find((el) => !el.isNextRoundBet);
    //
    // if (nextSection?.fighterNumber || nextSection?.fighterNumber === 0) {
    //   gameStore.setActiveCurrentSection(nextSection.fighterNumber);
    // }
  }

  resetGameStates() {
    this.betData = this.betData.map((el) => ({
      betId: null,
      fighterNumber: el.fighterNumber,
      amount: gameConfigStore.limits?.defaultBetAmount || gameConfigStore.limits.minBet,
      isNextRoundBet: false,
      isBetAccepted: false,
      freeBetId: null,
      freeBetMinOdd: null,
      winAmount: null,
      halfWinAmount: null,
      isHalfCashOut: false,
      finishedOdd: null,
      isDisabled: false,
    }));

    this.autoBetData = this.autoBetData.map((el) => ({
      fighterNumber: el.fighterNumber,
      isAutoBet: false,
      isOpened: false,
      autoBetCount: 0,
    }));

    this.autoCashOutData = this.autoCashOutData.map((el) => ({
      fighterNumber: el.fighterNumber,
      isOpened: false,
      autoCashOutOdd: minOdd,
    }));

    this.setWinData();
    this.updateTotalBetsAmount(0, true);
    notificationStore.setNotificationType(null);
  }

  handleDoBetResult(doBetData) {
    const {
      responseCode,
      id,
      amount,
      betData: { autoCashoutOdd, index },
    } = doBetData;
    const { isDemo } = applicationStore;

    const autoBetData = this.autoBetData.find((el) => el.fighterNumber === index);
    const { isAutoBet, autoBetCount } = autoBetData;
    this.setCurrentBetAmount(amount);

    if (index || index === 0) {
      this.updateBetAccepted(true, id, index);
      setTimeout(() => this.updateIsDisabledBetSection(false, index), isDemo ? 600 : 0);
      const isOpened = this.autoCashOutData.find((el) => el.fighterNumber === index);
      if (isOpened && autoCashoutOdd) {
        this.updateAutoCashOutData(autoCashOutDataKeys.autoCashOutOdd, autoCashoutOdd, index);
      }
    }

    const isBetAccepted = this.betData.find((el) => el.fighterNumber === index).isBetAccepted;

    if (isDemo) {
      gameStore.updatedBetCountData(betActionName.demoBetsCount);
    }
    // gameStore.autoUpdatedCurrentSection();

    notificationStore.setNotificationType({ responseCode });

    if (isBetAccepted && isAutoBet && autoBetCount) {
      this.updateAutoBetData(autoBetDataKeys.autoBetCount, autoBetCount - 1, index);
    }
  }

  setWinData(winAmount, jetIndex, isHalfCashOut = false) {
    if (winAmount && (jetIndex || jetIndex === 0) && !isHalfCashOut) {
      this.betData = this.betData.map((item) => {
        if (item.fighterNumber === jetIndex) {
          return {
            ...item,
            status: BetStatus.Win,
            isBetAccepted: false,
            winAmount,
          };
        }
        return item;
      });
    } else if (winAmount && isHalfCashOut) {
      this.betData = this.betData.map((item) => {
        if (item.fighterNumber === jetIndex) {
          return {
            ...item,
            halfWinAmount: winAmount,
          };
        }
        return item;
      });
    } else {
      this.betData = this.betData.map((item) => ({ ...item, winAmount: null }));
    }

    if (winAmount) {
      setTimeout(() => gameLiveConnection.getBalance(), 500);
    }
  }

  sendCashOut(betId, jetIndex, isHalf) {
    const autoBetData = this.autoBetData.find((el) => el.fighterNumber === jetIndex);
    const { isAutoBet, autoBetCount } = autoBetData;

    if (isHalf) {
      this.setIsHalfCashOut(true, jetIndex);
    } else {
      this.updateBetAccepted(false, betId, jetIndex);
    }
    if (isAutoBet && !autoBetCount) {
      this.updateAutoBetData(autoBetDataKeys.isAutoBet, false, jetIndex);
      this.updateAutoBetData(autoBetDataKeys.autoBetCount, 0, jetIndex);
      this.updateAutoBetData(autoBetDataKeys.isOpened, false, jetIndex);
    }
    if (!betStore.repliedBetData) {
      gameLiveConnection.cashOut({
        betId,
        roundId: roundInfoStore.roundId,
        cashoutDate: new Date(),
        cashOutHalf: this.betData.find((item) => item.fighterNumber === jetIndex).isHalfCashOut,
      });
    }
  }

  handleClearRoundData() {
    gameStore.updatedBetCountData();
    gameStore.setIsRoundFin(false);
    gameStore.setOddData(null);
    gameStore.setIsAnimationOffOn(true);
    gameStore.setCurrentRoundStatus(RoundStatus.OnFinish);
    gameStore.setCurrentRoundPassedSecond();
    gameStore.setFighterBlowAnim();
    gameStore.setInitialPassedSecond();
    gameStore.setIsBetsClosed(false);
    gameStore.setCurrentJetsInfo();
    this.betData = this.betData.map((item) => {
      const fighterAutoBetData = this.autoBetData.find(
        (el) => el.fighterNumber === item.fighterNumber
      );

      return {
        ...item,
        betId: null,
        isBetAccepted: false,
        finishedOdd: null,
        winAmount: null,
        halfWinAmount: null,
        isHalfCashOut: false,
        isDisabled:
          fighterAutoBetData.isAutoBet &&
          fighterAutoBetData.autoBetCount &&
          !applicationStore.isDemo
            ? true
            : item.isNextRoundBet,
      };
    });
  }
}

export const doBetStore = new DoBetStore();
